import { unzipSync, strFromU8 } from "fflate";
import { get } from "axios";
import { YEAR } from "../Constants";

/*
 * Checks the yearly appraisal Excel (e.g. "To Gaston_Appraisal 2026.xlsx")
 * against what is in the database. Read-only.
 *
 * Sheet "<YEAR>" (or the first sheet) needs a header row with:
 *   NAME | APPRAISER 1 (= proxy) | APPRAISER 2 (= appraiser) | DEPARTMENT |
 *   COMPANY | OCCUPATION (= position) | GD (= job grade)
 * Sheet "Login" (optional): No | SUPERVISOR <YEAR> | email | username
 *
 * Where a name is spelled differently (e.g. "LAU LI KUO, EDWIN" vs
 * "LAU LI KUO"), the database spelling is treated as correct and only noted.
 */

// ---------- xlsx reading (an .xlsx is a zip of XML files) ----------

const colIndex = (ref) => {
  const letters = ref.replace(/[0-9]/g, "");
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

const textOf = (el) =>
  Array.from(el.getElementsByTagName("t"))
    .filter((t) => t.parentNode.nodeName !== "rPh") // skip phonetic hints
    .map((t) => t.textContent)
    .join("");

// Returns { sheetName: [[cell, cell, ...], ...] } with cached values for formulas.
export const readXlsx = (arrayBuffer) => {
  const files = unzipSync(new Uint8Array(arrayBuffer));
  const xml = (path) => {
    const f = files[path];
    return f ? new DOMParser().parseFromString(strFromU8(f), "application/xml") : null;
  };

  const sst = xml("xl/sharedStrings.xml");
  const shared = sst ? Array.from(sst.getElementsByTagName("si")).map(textOf) : [];

  const rels = {};
  Array.from(xml("xl/_rels/workbook.xml.rels").getElementsByTagName("Relationship")).forEach(
    (r) => {
      const target = r.getAttribute("Target").replace(/^\/?(xl\/)?/, "");
      rels[r.getAttribute("Id")] = "xl/" + target;
    }
  );

  const sheets = {};
  Array.from(xml("xl/workbook.xml").getElementsByTagName("sheet")).forEach((s) => {
    const doc = xml(rels[s.getAttribute("r:id")]);
    if (!doc) return;
    sheets[s.getAttribute("name")] = Array.from(doc.getElementsByTagName("row")).map((row) => {
      const cells = [];
      Array.from(row.getElementsByTagName("c")).forEach((c) => {
        const type = c.getAttribute("t");
        const v = c.getElementsByTagName("v")[0];
        let value = null;
        if (type === "s") value = v ? shared[+v.textContent] : null;
        else if (type === "inlineStr") value = textOf(c);
        else if (type === "str" || type === "e") value = v ? v.textContent : null;
        else if (type === "b") value = v ? v.textContent === "1" : null;
        else value = v ? Number(v.textContent) : null;
        cells[colIndex(c.getAttribute("r"))] = value;
      });
      return cells;
    });
  });
  return sheets;
};

// ---------- comparison ----------

const norm = (s) => String(s ?? "").replace(/\s+/g, " ").trim().toUpperCase();
const val = (s) => String(s ?? "").replace(/\s+/g, " ").trim();
const loose = (s) => norm(s).replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
const beforeComma = (s) => norm(s).split(",")[0].trim();

const readStaffRows = (sheets) => {
  const sheetName = sheets[String(YEAR)] ? String(YEAR) : Object.keys(sheets)[0];
  const rows = sheets[sheetName];
  const headerIdx = rows.findIndex((r) => (r || []).some((c) => norm(c) === "NAME"));
  if (headerIdx < 0) throw new Error(`Sheet "${sheetName}": no header row with NAME`);
  const header = rows[headerIdx].map(norm);
  const col = {};
  const wanted = {
    name: "NAME",
    proxy: "APPRAISER 1",
    appraiser: "APPRAISER 2",
    department: "DEPARTMENT",
    company: "COMPANY",
    position: "OCCUPATION",
    grade: "GD",
  };
  Object.entries(wanted).forEach(([key, title]) => {
    col[key] = header.indexOf(title);
    if (col[key] < 0) throw new Error(`Sheet "${sheetName}": column "${title}" not found`);
  });
  const noCol = col.name - 1;
  const staff = [];
  rows.slice(headerIdx + 1).forEach((r, i) => {
    r = r || [];
    if (typeof r[noCol] !== "number" || !norm(r[col.name])) return;
    staff.push({
      line: headerIdx + i + 2,
      no: r[noCol],
      name: norm(r[col.name]),
      proxy: norm(r[col.proxy]),
      appraiser: norm(r[col.appraiser]),
      department: norm(r[col.department]),
      company: norm(r[col.company]),
      position: val(r[col.position]),
      grade: val(r[col.grade]),
    });
  });
  return { sheetName, staff };
};

const readLoginRows = (sheets) => {
  const rows = sheets["Login"];
  if (!rows) return [];
  const headerIdx = rows.findIndex((r) => (r || []).some((c) => norm(c).startsWith("SUPERVISOR")));
  if (headerIdx < 0) return [];
  const nameCol = rows[headerIdx].findIndex((c) => norm(c).startsWith("SUPERVISOR"));
  return rows
    .slice(headerIdx + 1)
    .filter((r) => r && typeof r[nameCol - 1] === "number" && norm(r[nameCol]))
    .map((r) => ({ name: norm(r[nameCol]), username: val(r[nameCol + 2]).toLowerCase() }));
};

export const checkExcelAgainstDb = async (file) => {
  console.log(`Reading ${file.name}...`);
  const sheets = readXlsx(await file.arrayBuffer());
  const { sheetName, staff } = readStaffRows(sheets);
  const logins = readLoginRows(sheets);
  console.log(`Sheet "${sheetName}": ${staff.length} staff rows; Login sheet: ${logins.length} supervisors`);

  console.log(`Loading database (year ${YEAR})...`);
  const [appRes, compRes, deptRes] = await Promise.all([
    get("api/appraisees"),
    get(`api/companies/year/${YEAR}`),
    get(`api/departments/year/${YEAR}`),
  ]);
  // api/appraisees returns every active user (all years) with their appraisers.
  const rowsForYear = appRes.data.filter((u) => +u.year === YEAR);
  const users = {};
  const appraiseeCount = {};
  rowsForYear.forEach((u) => {
    users[u.userid] = u;
    if (u.appraisers) appraiseeCount[u.userid] = (appraiseeCount[u.userid] || 0) + 1;
  });
  const userList = Object.values(users);

  const index = (keyFn) => {
    const m = {};
    userList.forEach((u) => {
      const k = keyFn(u.fullname);
      (m[k] = m[k] || []).push(u);
    });
    return m;
  };
  const byNorm = index(norm);
  const byLoose = index(loose);
  const byPrefix = index(beforeComma);
  // Returns { users: [..], similar: bool } for a name from the Excel.
  const match = (name) => {
    if (byNorm[name]) return { users: byNorm[name], similar: false };
    for (const [m, key] of [
      [byLoose, loose(name)],
      [byPrefix, beforeComma(name)],
      [byNorm, beforeComma(name)],
      [byPrefix, name],
    ]) {
      if (m[key] && m[key].length === 1) return { users: m[key], similar: true };
    }
    return { users: [], similar: false };
  };

  const groupsOf = (data) => {
    const names = new Set();
    const of = {};
    data.forEach((g) => {
      names.add(norm(g.name));
      (g.staffs || []).forEach((s) => (of[s.userid] = of[s.userid] || []).push(norm(g.name)));
    });
    return { names, of };
  };
  const companies = groupsOf(compRes.data);
  const departments = groupsOf(deptRes.data);
  console.log(
    `Database: ${userList.length} users, ${Object.keys(appraiseeCount).length} appraisees, ${companies.names.size} companies, ${departments.names.size} departments`
  );

  const problems = {};
  const notes = {};
  const add = (bucket, category, row) => (bucket[category] = bucket[category] || []).push(row);
  const spelling = {};
  const noteSpelling = (excel, user) => {
    if (norm(user.fullname) !== excel) spelling[excel] = user.fullname;
  };
  const resolve = (name) => {
    if (!name) return null;
    const m = match(name);
    if (m.users.length === 1) {
      noteSpelling(name, m.users[0]);
      return m.users[0];
    }
    return m.users.length > 1 ? "many" : null;
  };

  const counts = {};
  staff.forEach((r) => (counts[r.name] = (counts[r.name] || 0) + 1));
  Object.entries(counts)
    .filter(([, n]) => n > 1)
    .forEach(([name, n]) => add(problems, "Name listed more than once in Excel", { name, times: n }));

  const seen = new Set();
  staff.forEach((r) => {
    const tag = { no: r.no, name: r.name };
    const user = resolve(r.name);
    if (user === "many") return add(problems, "More than one user with this name", tag);
    if (!user) return add(problems, "Staff not found in users", tag);
    seen.add(user.userid);

    if (val(user.position) !== r.position)
      add(problems, "Position differs", { ...tag, excel: r.position, database: val(user.position) });
    if (val(user.job_grade) !== r.grade)
      add(problems, "Grade differs", { ...tag, excel: r.grade, database: val(user.job_grade) });

    if (!user.appraisers) {
      add(problems, "Not in appraisees", tag);
    } else {
      if (appraiseeCount[user.userid] > 1) add(problems, "Appraisee listed more than once", tag);
      const a = user.appraisers[0] || {};
      [
        ["Appraiser (APPRAISER 2) differs", r.appraiser, a.appraiser],
        ["Proxy (APPRAISER 1) differs", r.proxy, a.proxy],
      ].forEach(([category, excelName, dbPerson]) => {
        const expected = resolve(excelName);
        const expectedId = expected && expected !== "many" ? expected.userid : null;
        const actualId = dbPerson?.userid || null;
        if (excelName && !expectedId) {
          add(problems, `${category.split(" ")[0]} not found in users`, { ...tag, [category.split(" ")[0].toLowerCase()]: excelName });
        } else if (expectedId !== actualId) {
          add(problems, category, { ...tag, excel: excelName, database: dbPerson?.fullname || "" });
        }
      });
    }

    [
      ["Company", companies, r.company],
      ["Department", departments, r.department],
    ].forEach(([label, groups, want]) => {
      const got = groups.of[user.userid] || [];
      if (!groups.names.has(want)) add(problems, `${label} doesn't exist in database`, { ...tag, [label.toLowerCase()]: want });
      else if (got.length !== 1 || got[0] !== want)
        add(problems, `${label} differs`, { ...tag, excel: want, database: got.join(" | ") || "(none)" });
    });
  });

  userList
    .filter((u) => u.appraisers && !seen.has(u.userid))
    .forEach((u) => add(problems, "Appraisee in database but not in Excel", { name: u.fullname }));
  [
    ["company", companies],
    ["department", departments],
  ].forEach(([label, groups]) => {
    Object.entries(groups.of)
      .filter(([userid]) => !seen.has(userid))
      .forEach(([userid, g]) =>
        add(problems, `In a database ${label} but not in Excel`, { name: users[userid]?.fullname || userid, [label]: g.join(" | ") })
      );
  });

  logins.forEach((l) => {
    const user = resolve(l.name);
    if (!user || user === "many") add(problems, "Login sheet: supervisor not found in users", { name: l.name });
    else if (val(user.username).toLowerCase() !== l.username)
      add(notes, "Login sheet: username differs (database is used)", { name: l.name, excel: l.username, database: user.username });
  });

  Object.entries(spelling).forEach(([excel, database]) =>
    add(notes, "Spelled differently (database spelling is used)", { excel, database })
  );

  const summarize = (bucket) => Object.entries(bucket).map(([category, rows]) => ({ category, count: rows.length }));
  const log = (bucket, fn) =>
    Object.entries(bucket).forEach(([category, rows]) => {
      fn(`${category}: ${rows.length}`);
      console.table(rows);
    });

  const problemTotal = Object.values(problems).reduce((n, r) => n + r.length, 0);
  if (problemTotal === 0) console.log(`All ${staff.length} staff match the database.`);
  else log(problems, console.error);
  log(notes, console.warn);

  return {
    summary:
      problemTotal === 0
        ? `all ${staff.length} staff match the database`
        : `${problemTotal} problem(s) in ${Object.keys(problems).length} categories`,
    report: { problems: summarize(problems), notes: summarize(notes) },
  };
};
